import { useContext, useEffect, useRef, useState } from 'react'
import { captureChartInteraction, useChartsToolOpenState } from '../../analytics/chartsTool';
import { URL } from '../../common/api/urls';
import { useAxiosWithAuth } from '../../common/api/hooks/useAxiosWithAuth';
import { Box,  Typography,  CircularProgress } from '@mui/material';
import { useNavigate,  } from 'react-router-dom';
import PositionSeriesTable from './PositionSeriesTable';
import dayjs from 'dayjs';
import { DatePicker } from 'antd';
import { TEXT_MSGS } from '../../common/utility/constant';
import { styled, useTheme } from '@mui/material/styles';
import { ToolbarFixed } from '../../layouts/component.js';
import AppErrorPopup from '../../components/AppErrorPopup';
import useAuth from '../../common/hooks/useAuth';
// ============================================================
// NEW - Centralized Permission System
// See: src/common/hooks/usePermissions.js
// ============================================================
import usePermissions from '../../common/hooks/usePermissions';
import SubscriptionPopup from './components/subscription_popup';

import { Table, Card } from "antd";
import "./styles/styles.scss"; // Custom CSS
import { LOCALE_STRING } from "../../common/utility/constant";
import { applySteelCardTool } from './components/free_tool_steel_card_config.js';
import { UserSettingsContext } from '../../setup/routes-manager/RequireAuth.js';

const DateFormat = 'DD-MM-YYYY';
// const FooterToolbar = styled(ToolbarFixed)(
//     ({ theme }) => `
//     bottom:0px;
//     justify-content:center;
//     text-align:center;
//     `
// )

// Function to apply background color only to Net values
const getNetStyle = (value, reverse) => ({
    color: reverse ? (value > 0 ? "red" : "green") : (value < 0 ? "red" : "green"),
    fontWeight: "bold",
    backgroundColor: reverse ? (value > 0 ? "#ffcccc" : "#ccffcc") : (value < 0 ? "#ffcccc" : "#ccffcc"), // Red for negative, Green for positive
    padding: "5px",
    textAlign: "right",
});

const getNumberStyle = (value) => ({
    padding: "5px",
    textAlign: "right",
});


// Futures Table with Common Header
const futuresColumns = [
    {
        title: "Futures", 
        children: [
            {
                title: "",
                dataIndex: "label",
                key: "label",
                align: "left",
                render: (text) => <div style={{ fontWeight: "bold" }}>{text}</div>,
            },
            {
                title: "",
                dataIndex: "futures",
                key: "futures",
                align: "right",
                render: (value) => <div style={getNumberStyle(value)}>{Number(value).toLocaleString(LOCALE_STRING)}</div>
            },
            {
                title: "",
                dataIndex: "net",
                key: "net",
                render: (value) =>
                    value !== null
                        ? <div style={getNetStyle(value, false)}>{Number(value).toLocaleString()}</div>
                        : <div style={{ fontWeight: 'bold' }}>NET</div>,
            },
        ],
    },
];


// CE and PE Table with Common Headers
const optionsColumns = [
    {
        title: "CE", 
        children: [
            {
                title: "", dataIndex: "ceLabel", key: "ceLabel", align: "left",
                render: (text) => <div style={{ fontWeight: "bold" }}>{text}</div>,
            },
            {
                title: "", dataIndex: "ceValue", key: "ceValue", align: "right",
                render: (value) => <div style={getNumberStyle(value)}>{Number(value).toLocaleString(LOCALE_STRING)}</div>
            },
            {
                title: "",
                dataIndex: "ceNet",
                key: "ceNet",

                render: (value) =>
                    value !== null ? <div style={getNetStyle(value, false)}>{Number(value).toLocaleString(LOCALE_STRING)}</div> : <div style={{ fontWeight: 'bold' }}>NET</div>, // Background color only for Net
            },
        ],
    },

    {
        title: "PE",
        children: [
            {
                title: "", dataIndex: "peLabel", key: "peLabel", align: "left",
                render: (text) => <div style={{ fontWeight: "bold" }}>{text}</div>,
            },
            {
                title: "", dataIndex: "peValue", key: "peValue", align: "right",
                render: (value) => <div style={getNumberStyle(value)}>{Number(value).toLocaleString(LOCALE_STRING)}</div>
            },
            {
                title: "",
                dataIndex: "peNet",
                key: "peNet",

                render: (value) =>
                    value !== null ? <div style={getNetStyle(value, true)}>{Number(value).toLocaleString(LOCALE_STRING)}</div> : <div style={{ fontWeight: 'bold' }}>NET</div>, // Background color only for Net
            },
        ],
    },

];
const getColorForTitle = (title) => {
    if (title === 'FII' || title === 'DII') return '#fff4bd';
    return '#ffc552';
};

const transformApiDataToSections = (apiData) => {
    const participantData = apiData.participant_data;

    return Object.entries(participantData).map(([title, values]) => {
        const longFut = values.long_doi;
        const shortFut = values.short_doi;
        const netFut = (longFut ?? 0) - (shortFut ?? 0);

        const ceLong = values.ce_long_doi;
        const ceShort = values.ce_short_doi;
        const peLong = values.pe_long_doi;
        const peShort = values.pe_short_doi;

        return {
            title,
            color: getColorForTitle(title),
            data: [
                { key: 1, label: 'Longs', futures: longFut, net: null },
                { key: 2, label: 'Shorts', futures: shortFut, net: netFut },
            ],
            options: [
                {
                    key: 1,
                    ceLabel: 'Longs',
                    ceValue: ceLong,
                    ceNet: null,
                    peLabel: 'Longs',
                    peValue: peLong,
                    peNet: null,
                },
                {
                    key: 2,
                    ceLabel: 'Shorts',
                    ceValue: ceShort,
                    ceNet: ceLong - ceShort,
                    peLabel: 'Shorts',
                    peValue: peShort,
                    peNet: peLong - peShort,
                },
            ],
        };
    });
};
export default function PositionTable() {
    const navigate = useNavigate();
    const theme = useTheme();
    const from = "/login";
    const { auth } = useAuth();
    // ============================================================
    // NEW - Centralized Permission System
    // See: src/common/utility/permissionService.js
    // ============================================================
    const permissions = usePermissions();
    const {setSteelCardTool} = useContext(UserSettingsContext)
    // ============================================================
    // OLD - Hardcoded role checks (DEPRECATED)
    // Replaced by: permissions service methods
    // See: src/common/utility/permissionService.js
    // ============================================================
    // const ALLOWED_ROLES = [
    //     USER_ROLE_FIELDS.PRO,
    //     USER_ROLE_FIELDS.PRO2,
    //     USER_ROLE_FIELDS.ADMIN,
    //     USER_ROLE_FIELDS.FAST_DATA,
    //     USER_ROLE_FIELDS.ORDERFLOW
    // ];
    // const isBasicUser = !auth?.role?.some(role => role === USER_ROLE_FIELDS.FAST_DATA);
    // const hasAccess = Array.isArray(auth?.role) 
    //     ? auth.role.some(role => ALLOWED_ROLES.includes(role))
    //     : ALLOWED_ROLES.includes(auth?.role);
    
    // NEW - Use centralized permission service
    const isBasicUser = permissions.isFreeUser();
    const hasAccess = permissions.isPremiumUser() || permissions.isEliteUser() || permissions.isAdmin();
    const [cancel, responseData, error, loaded, reset, executeAPI] = useAxiosWithAuth();
    useChartsToolOpenState(Boolean(loaded && responseData), Boolean(loaded && error && !responseData));
    const [fiiState, setFiiState] = useState({
        date: undefined,
        isLoading: true,
        sections: undefined,
        seriesSections: undefined,
        dateRange: [],
        date_key: "NA"
    });
    const { sections, seriesSections, dateRange, isLoading, date, date_key } = fiiState
    const emptyStateCapturedRef = useRef(false);
    useEffect(() => {
      if (isLoading) {
        return;
      }
      const noCurrentData = !sections;
      const noSeriesData = hasAccess && !(seriesSections && dateRange?.length);
      if (!noCurrentData && !noSeriesData) {
        emptyStateCapturedRef.current = false;
        return;
      }
      if (emptyStateCapturedRef.current) {
        return;
      }
      emptyStateCapturedRef.current = true;
      captureChartInteraction('charts_empty_state_seen', {
        load_time_ms: 0,
        error_category: 'empty',
        error_code: 'no_data',
      });
    }, [isLoading, sections, hasAccess, seriesSections, dateRange]);

    // State for upgrade popup
    const [showUpgradePopup, setShowUpgradePopup] = useState(false);

    const [msgState, setMsgState] = useState({
        open: false,
        msg: "",
        severity: "info"
    });

    const { open, msg, severity } = msgState;
    /**
       * autoclosing of the snackbar msg bar 
   */
    const handleClose = (event, reason) => {
        setMsgState({ ...msgState, open: false });
    };

    useEffect(() => {
        document.title = "Participantwise Positions";
        var link = !document.querySelector("link[rel='canonical']") ? document.querySelector("link[rel='canonical']") : document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', document.location.protocol + '//' + document.location.host + document.location.pathname);
        document.head.appendChild(link);
    }, []);

    useEffect(() => {
        executeAPI(`${URL.POSITION_DATA}?dt=&ser=1`, "GET", {})
    }, [])

    useEffect(() => {
        if (loaded) {
            console.log('API Response:', responseData);
            if (responseData != null) {
                applySteelCardTool(responseData,setSteelCardTool);

                let transformed = undefined;
                if(responseData.participant_data) {
                    transformed = transformApiDataToSections(responseData);
                }
                
                console.log('Series Data:', responseData.series_data);
                console.log('Date Range:', responseData.date_range);
                
                setFiiState((prevState) => ({
                    ...prevState,
                    sections: transformed,
                    seriesSections: responseData.series_data,
                    dateRange: responseData.date_range,
                    isLoading: false,
                    date_key: responseData.date_key
                }))
            }
            else if (error !== null) {


                setMsgState({ open: true, msg: error?.response?.data?.message ?? `${TEXT_MSGS.NETWORK_ERROR_MSG}`, severity: "error" });
                //if unauthorized then redirec it to login page
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    console.log("status received =", error?.response?.status)
                    navigate(from, { replace: true });
                }
            }
            reset();
        }
    }, [loaded, responseData]);

    const onChange = (date1, dateString) => {
        // If user is a basic user, show upgrade popup
        if (isBasicUser) {
            setShowUpgradePopup(true);
            return;
        }

        setFiiState((prevState) => ({
            ...prevState,
            date: dayjs(date1)
        }))
        let tempDate = dayjs(date1).format(DateFormat)
        executeAPI(`${URL.POSITION_DATA}?dt=${tempDate}&ser=1`, "GET", {})
    };

    // Handle closing the subscription popup
    const handleClosePopup = () => {
        setShowUpgradePopup(false);
    };

    const disabledDate = (current) => {
        return (current && current >= dayjs().endOf('day')) || dayjs(current).day() == 6 || dayjs(current).day() == 0
    }


    return (
        <>
            {!isLoading ? (
                <div style={{ padding: "20px", width: "100%" }}>
                    <Box display={'flex'} flexDirection={'row'}>
                        <DatePicker 
                            onChange={onChange}
                            allowClear={false}
                            disabledDate={disabledDate}
                            value={date}
                            format={DateFormat}
                            style={{
                                border: "1px solid #415e81",
                                background: "#022D5A",
                                borderRadius: "4px",
                                width: 200,
                                marginLeft: 20,
                                marginTop: "12px"
                            }}
                        />
                        <Typography sx={{ fontSize: 16, mr: 2, ml: 4, mt: 2 }}>
                            Selected Date: {date_key}
                        </Typography>
                    </Box>

                    {/* Current Data */}
                    {sections ? (
                        <>
                            <div className="header">Participant Wise Position Changes</div>
                            <div className="grid-container">
                                {sections.map((section, index) => (
                                    <Card
                                        key={index}
                                        className="section-card"
                                        style={{ backgroundColor: section.color }}
                                    >
                                        <div className="section-title">{section.title}</div>
                                        <div style={{ width: "50%" }}>
                                            <Table
                                                columns={futuresColumns}
                                                dataSource={section.data}
                                                pagination={false}
                                                bordered
                                                size="small"
                                                className="custom-table"
                                            />
                                        </div>
                                        <Table
                                            columns={optionsColumns}
                                            dataSource={section.options}
                                            pagination={false}
                                            bordered
                                            size="small"
                                            className="custom-table"
                                            title={() => (
                                                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                                                    Options
                                                </div>
                                            )}
                                        />
                                    </Card>
                                ))}
                            </div>

                            {/* Series Data Table */}
                            <Box sx={{ mt: 5 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Monthly Series wise Positions
                                </Typography>
                                
                                {/* Temporary Role Display Panel 
                                <Box sx={{ mb: 2, p: 1, bgcolor: '#001E3C', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ color: '#90caf9' }}>
                                        Current Roles: {auth?.role?.map(role => {
                                            const roleMap = {
                                                'PRO': 'Pro',
                                                'PRO2': 'Pro 2',
                                                'ADMIN': 'Admin',
                                                'FAST_DATA': 'Fast Data',
                                                'ORDERFLOW': 'Order Flow'
                                            };
                                            return roleMap[role] || role;
                                        }).join(', ') || 'No roles'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#90caf9', mt: 0.5 }}>
                                        Is Basic User: {isBasicUser ? 'Yes' : 'No'} | 
                                        Has Access: {hasAccess ? 'Yes' : 'No'}
                                    </Typography>
                                </Box>
                                */}
                                {!hasAccess ? (
                                    <Box sx={{
                                        p: 3,
                                        textAlign: 'center',
                                        bgcolor: '#022D5A',
                                        borderRadius: 1,
                                        border: '1px solid #415e81'
                                    }}>
                                        <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
                                            Premium Feature
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#90caf9', mb: 2 }}>
                                            Upgrade your account to see historical participant data.
                                        </Typography>
                                        
                                    </Box>
                                ) : seriesSections && dateRange && dateRange.length > 0 ? (
                                    <PositionSeriesTable 
                                        seriesSections={seriesSections} 
                                        dateRange={dateRange}
                                    />
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#022D5A', borderRadius: 1, border: '1px solid #415e81' }}>
                                        <Typography variant="body1" sx={{ color: '#90caf9' }}>
                                            No data available for the selected date.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </>
                    ) : (
                        <div style={{ textAlign: "center", width: "100%" }}>
                            <Typography sx={{ fontSize: 16, marginTop: "10%" }}>
                                Data not present for selected date
                            </Typography>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ textAlign: "center", width: "100%" }}>
                    <CircularProgress sx={{ marginTop: "10%" }} />
                </div>
            )}
            <AppErrorPopup
              open={open}
              message={msg}
              severity={severity}
              onClose={handleClose}
              showCloseX
            />
            {/* Legacy snackbar (replaced by AppErrorPopup)
            <Snackbar onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={open} autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION_SHORT} >
                <Alert severity={severity} sx={{ marginTop: "48px", width: { mobile: '80%', tablet: "70%", laptop: "40%" } }}>
                    {msg}
                </Alert>
            </Snackbar>
            */}
            {showUpgradePopup && (
                <SubscriptionPopup 
                    open={showUpgradePopup} 
                    onClose={handleClosePopup}
                />
            )}
        </>
    )
}